import {StreamComponentType} from '@Components/ai-chat-window/lib/stream-component-registry.types';
import {defineStreamComponentRegistration} from '@Components/ai-chat-window/lib/stream-component-registration';
import {isOptionalString, isPlainObject, isShownMoment, parseEachOrNull, parseOptionalList} from '@Components/ai-chat-window/lib/stream-component-prop-guards';
import {parseEntityDraftCardProps} from '@Components/ai-chat-window/components/entity-draft-card/entity-draft-card.library';
import type {EntityDraftApproveOption, EntityDraftGroupProps, EntityDraftLeftOutItem, EntityDraftMessageAction} from './entity-draft-group.types';
import {EntityDraftGroup} from './entity-draft-group';

const parseLeftOutItem = (props: Record<string, unknown>): EntityDraftLeftOutItem | null => {
  if (typeof props.label !== 'string' || typeof props.reason !== 'string') {
    return null;
  }

  return {label: props.label, reason: props.reason};
};

const parseApproveOption = (props: Record<string, unknown>): EntityDraftApproveOption | null => {
  if (typeof props.id !== 'string' || typeof props.label !== 'string' || typeof props.primary !== 'boolean') {
    return null;
  }

  return {id: props.id, label: props.label, primary: props.primary, moment: isShownMoment(props.moment) ? props.moment : undefined};
};

/** Options that do not parse leave the card with its one approve button, as a card from before options. */
const parseApproveOptions = (value: unknown): EntityDraftApproveOption[] | undefined => {
  return parseOptionalList(value, parseApproveOption) ?? undefined;
};

const parseMessageAction = (value: unknown): EntityDraftMessageAction | undefined => {
  if (!isPlainObject(value) || typeof value.label !== 'string' || typeof value.message !== 'string') {
    return undefined;
  }

  return {label: value.label, message: value.message};
};

export const entityDraftGroupRegistration = defineStreamComponentRegistration(
  StreamComponentType.ENTITY_DRAFT_GROUP,
  EntityDraftGroup,
  (props: Record<string, unknown>): EntityDraftGroupProps | null => {
    if (!isOptionalString(props.title) || !isOptionalString(props.approveLabel) || !isOptionalString(props.note) || !Array.isArray(props.cards) || props.cards.length === 0) {
      return null;
    }

    const cards = parseEachOrNull(props.cards, parseEntityDraftCardProps);
    if (cards === null) {
      return null;
    }

    const leftOut = parseOptionalList(props.leftOut, parseLeftOutItem);
    if (leftOut === null) {
      return null;
    }

    return {
      title: props.title,
      note: props.note,
      noteMoment: isShownMoment(props.noteMoment) ? props.noteMoment : undefined,
      cards,
      leftOut,
      approveLabel: props.approveLabel,
      approvedAlone: props.approvedAlone === true ? true : undefined,
      approveOptions: parseApproveOptions(props.approveOptions),
      messageAction: parseMessageAction(props.messageAction),
    };
  }
);
